import { useState, useCallback, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { useSonnerToast } from '@/shared/ui';
import { reportError } from '@/shared/ui/components/basic/error-notification/report-error';
import { LaunchesService } from '../../services/launchesApi';
import type { Launch } from '../../types';
import { buildLaunchFormSchema, type CreateLaunchFormData } from '../create-launch/schema';

export interface UseEditLaunchFormProps {
  agencyId: string;
  launch: Launch | null;
  onSuccess?: () => void;
}

/**
 * Form controller for the edit-launch drawer — metadata only
 * (name/address/hold TTL); unit inventory is managed separately.
 */
export function useEditLaunchForm({ agencyId, launch, onSuccess }: UseEditLaunchFormProps) {
  const { t } = useTranslation('features/launches');
  const { showSuccess } = useSonnerToast();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const schema = useMemo(() => buildLaunchFormSchema(t), [t]);

  const form = useForm<CreateLaunchFormData>({
    resolver: zodResolver(schema),
    values: {
      name: launch?.name ?? '',
      address: launch?.address ?? '',
      holdTtlMinutes: launch ? Math.round(launch.hold_ttl_seconds / 60) : 15,
    },
    mode: 'onSubmit',
  });

  const onValid = useCallback(async (data: CreateLaunchFormData) => {
    if (!launch) return;
    setIsSubmitting(true);
    try {
      await LaunchesService.update(agencyId, launch.launch_id, {
        name: data.name,
        address: data.address || undefined,
        hold_ttl_seconds: data.holdTtlMinutes * 60,
      });
      showSuccess(t('edit.success'));
      onSuccess?.();
    } catch (error) {
      reportError(error, { message: t('errors.generic') });
    } finally {
      setIsSubmitting(false);
    }
  }, [agencyId, launch, onSuccess, showSuccess, t]);

  const handleSubmit = useCallback(
    () => form.handleSubmit(onValid)(),
    [form, onValid],
  );

  return { form, isSubmitting, handleSubmit };
}
